'use client';

import { memo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Handle, Position, NodeProps, useEdges } from 'reactflow';
import { Spinner, CheckCircle, XCircle, X, Stack } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { useWorkflowStore } from '../../../stores/workflowStore';
import { useUploadedFileStore } from '../../../stores/uploadedFileStore';
import { NODE_META } from '../nodeTypes';
import { getNodeDisplayLabel } from '../../../utils/nodeDisplayLabel';

// ── Node role classification ────────────────────────────────────────────────
/** Source-only nodes: no incoming connections (only a bottom output handle) */
const INPUT_TYPES = new Set(['load_excel', 'load_csv', 'load_table', 'load_api', 'online_sheet']);
/** Sink-only nodes: no outgoing connections (only a top input handle) */
const OUTPUT_TYPES = new Set(['export']);

/** Multi-input nodes: can accept unlimited inputs */
const MULTI_INPUT_TYPES = new Set(['join', 'append', 'diff']);

// ── Status styles ───────────────────────────────────────────────────────────
const statusStyles = {
  idle:    { border: 'border-zinc-200',    shadow: 'shadow-sm',                       glow: '' },
  running: { border: 'border-emerald-400', shadow: 'shadow-lg shadow-emerald-500/20', glow: 'ring-2 ring-emerald-500/30' },
  success: { border: 'border-emerald-500', shadow: 'shadow-lg shadow-emerald-500/10', glow: '' },
  error:   { border: 'border-red-400',     shadow: 'shadow-lg shadow-red-500/10',     glow: 'ring-2 ring-red-500/30' },
};

const statusBarColor = {
  idle: '', running: 'bg-emerald-400', success: 'bg-emerald-500', error: 'bg-red-400',
};

const StatusIcon = {
  idle: null, running: Spinner, success: CheckCircle, error: XCircle,
} as const;

const statusIconClass = {
  idle: '', running: 'text-emerald-500 animate-spin', success: 'text-emerald-500', error: 'text-red-500',
} as const;

// ── Shared handle className ─────────────────────────────────────────────────
const HANDLE_CLS = [
  '!w-3 !h-3 !rounded-full',
  '!bg-white !border-2 !border-zinc-300',
  'hover:!scale-125 hover:!border-emerald-400 hover:!bg-emerald-50',
  'transition-all duration-150',
].join(' ');

// ── Spotlight hook ──────────────────────────────────────────────────────────
function useSpotlight() {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ x: 50, y: 50 });
  const onMouseMove = (e: React.MouseEvent) => {
    if (!ref.current) return;
    const r = ref.current.getBoundingClientRect();
    setPos({ x: ((e.clientX - r.left) / r.width) * 100, y: ((e.clientY - r.top) / r.height) * 100 });
  };
  return { ref, pos, onMouseMove };
}

// ── Component ───────────────────────────────────────────────────────────────
interface OperatorNodeData {
  label: string;
  config: { type: string; params: Record<string, unknown> };
  status?: 'idle' | 'running' | 'success' | 'error';
  rowCount?: number;
  /** When true, hide delete button (e.g. in version diff read-only canvas) */
  readOnly?: boolean;
}

export const OperatorNode = memo(function OperatorNode({ id, data, type, selected }: NodeProps<OperatorNodeData>) {
  const nodeType = data.config?.type || type || 'filter';
  const meta     = NODE_META[nodeType];
  const Icon     = meta?.icon;
  const status   = data.status ?? 'idle';
  const SIcon    = StatusIcon[status];
  const { ref, pos, onMouseMove } = useSpotlight();
  const { removeNode, setSelectedNode } = useWorkflowStore();
  const { t, i18n } = useTranslation();

  const isInput  = INPUT_TYPES.has(nodeType);
  const isOutput = OUTPUT_TYPES.has(nodeType);
  const isMultiInput = MULTI_INPUT_TYPES.has(nodeType);
  
  // Get current input connections count for multi-input nodes
  const edges = useEdges();
  const incomingEdges = edges.filter((e) => e.target === id);
  const currentInputCount = incomingEdges.length;

  const { files, clearFileForNode } = useUploadedFileStore();
  const nodeFile = isInput ? (files[id] ?? null) : null;

  const accentClass = meta?.bg.replace('-50', '-400') ?? 'bg-zinc-300';
  /** 选中时高亮（编辑态与只读对比页均高亮，便于对比页点击节点时可见） */
  const showSelectedStyle = selected;

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Clear any uploaded file associated with this node before removing it
    if (isInput) clearFileForNode(id);
    removeNode(id);
    setSelectedNode(null);
  };

  return (
    <motion.div
      ref={ref}
      onMouseMove={onMouseMove}
      initial={{ scale: 0.88, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ type: 'spring', stiffness: 220, damping: 22 }}
      className={[
        'relative group min-w-[176px] bg-white rounded-xl overflow-visible',
        'border-2', statusStyles[status].border,
        showSelectedStyle ? 'ring-2 ring-emerald-500 ring-offset-1' : '',
        statusStyles[status].shadow, statusStyles[status].glow,
        'transition-colors duration-200',
      ].join(' ')}
    >
      {/* ── Top "input" handle (hidden for source nodes) ── */}
      {!isInput && (
        <Handle
          type="target"
          position={Position.Top}
          className={HANDLE_CLS}
          style={{ top: -6 }}
        />
      )}

      {/* Spotlight hover glow */}
      <div
        className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
        style={{
          background: `radial-gradient(260px circle at ${pos.x}% ${pos.y}%, rgba(16,185,129,0.08), transparent 60%)`,
        }}
      />

      {/* Running status bar (top edge) */}
      {status !== 'idle' && (
        <motion.div
          className={`absolute top-0 left-0 right-0 h-0.5 rounded-t-xl ${statusBarColor[status]}`}
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: 0.25 }}
        />
      )}

      {/* Left accent strip */}
      {meta && <div className={`absolute left-0 top-0 bottom-0 w-1 rounded-l-xl ${accentClass}`} />}

      {/* Multi-input indicator — top-right, always visible for multi-input nodes */}
      {isMultiInput && (
        <div
          className="absolute top-1.5 right-1.5 z-10 flex items-center gap-1 px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-500 group-hover:opacity-0 transition-opacity duration-150"
          title={t('nodes.multiInput', '多输入')}
        >
          <Stack className="w-3 h-3" weight="fill" />
          <span className="text-[9px] font-medium">{currentInputCount > 0 ? currentInputCount : ''}</span>
        </div>
      )}

      {/* Delete button — top-right, shown on hover (hidden in readOnly mode) */}
      {!data.readOnly && (
        <motion.button
          onClick={handleDelete}
          whileHover={{ scale: 1.15 }}
          whileTap={{ scale: 0.9 }}
          className="absolute top-1.5 right-1.5 z-20 w-4 h-4 rounded-full bg-zinc-100 hover:bg-red-100 text-zinc-400 hover:text-red-500 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-150"
          title={t('nodes.delete')}
        >
          <X className="w-2.5 h-2.5" weight="bold" />
        </motion.button>
      )}

      {/* Content */}
      <div className="relative z-10 pl-3 pr-3 py-2.5 overflow-hidden rounded-xl">
        <div className="flex items-center gap-2.5">
          {/* Icon badge */}
          {Icon && meta && (
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${meta.bg}`}>
              <Icon className={`w-4 h-4 ${meta.color}`} weight="fill" />
            </div>
          )}

          {/* Label + row count */}
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-zinc-900 truncate leading-tight">
              {getNodeDisplayLabel(nodeType, data.label, t, i18n)}
            </p>
            {data.rowCount !== undefined && (
              <p className="text-[10px] text-zinc-400 font-mono">{data.rowCount.toLocaleString()} {t('nodes.rows')}</p>
            )}
            {/* Input node: show uploaded file name or "待上传" hint */}
            {isInput && nodeFile ? (
              <span
                className="inline-block mt-0.5 px-1.5 py-px text-[9px] font-medium bg-emerald-50 text-emerald-600 rounded truncate max-w-[120px]"
                title={`${nodeFile.name}（${nodeFile.rowCount.toLocaleString()} ${t('nodes.rows')}）`}
              >
                {nodeFile.name}
              </span>
            ) : isInput ? (
              <span className="inline-block mt-0.5 px-1.5 py-px text-[9px] font-medium bg-amber-50 text-amber-500 rounded">
                {t('nodes.pendingUpload')}
              </span>
            ) : null}
            {isOutput && (
              <span className="inline-block mt-0.5 px-1.5 py-px text-[9px] font-medium bg-zinc-100 text-zinc-500 rounded">
                {t('nodes.output')}
              </span>
            )}
          </div>

          {/* Status icon */}
          {SIcon && (
            <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', stiffness: 400 }}>
              <SIcon className={`w-4 h-4 flex-shrink-0 ${statusIconClass[status]}`} weight={status === 'running' ? 'bold' : 'fill'} />
            </motion.div>
          )}
        </div>

        {/* Config params preview */}
        {data.config?.params && Object.keys(data.config.params).length > 0 && (
          <div className="mt-2 pt-2 border-t border-zinc-100 flex flex-wrap gap-1">
            {Object.entries(data.config.params).slice(0, 3).map(([key]) => (
              <span key={key} className="px-1.5 py-0.5 text-[9px] font-mono bg-zinc-100 text-zinc-500 rounded truncate max-w-[80px]">
                {key}
              </span>
            ))}
            {Object.keys(data.config.params).length > 3 && (
              <span className="px-1.5 py-0.5 text-[9px] text-zinc-400">
                +{Object.keys(data.config.params).length - 3}
              </span>
            )}
          </div>
        )}
      </div>

      {/* ── Bottom "output" handle (hidden for sink nodes) ── */}
      {!isOutput && (
        <Handle
          type="source"
          position={Position.Bottom}
          className={HANDLE_CLS}
          style={{ bottom: -6 }}
        />
      )}

      {/* Selected ring overlay（只读模式下不显示，与编辑工作流风格一致） */}
      {showSelectedStyle && (
        <motion.div
          className="absolute -inset-px rounded-xl border-2 border-emerald-500 pointer-events-none"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.15 }}
        />
      )}
    </motion.div>
  );
});

OperatorNode.displayName = 'OperatorNode';
